import axios from "axios";
import { useAtom } from "jotai";
import { IS_LOGGED_IN, SELECTED_EXPERT_INDEX } from "../pages/AtomStates"; // AtomStates 파일에서 IS_LOGGED_IN 임포트

const liveUrl = "https://wishresearch.kr";
const localUrl = "http://localhost:8000";

// 환경에 따라 자동으로 URL 설정
// const serverUrl = process.env.NODE_ENV === "development" ? localUrl : liveUrl;

const serverUrl = liveUrl;

export const fetchDataById = async (id) => {
  try {
    const apiUrl = `${serverUrl}/data/${id}`; // 숫자에 해당하는 데이터 가져오기
    const response = await axios.get(apiUrl);
    return response.data;
  } catch (error) {
    console.error("Error fetching data from server:", error);
    throw error;
  }
};

export const openDB = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open("MyAppDB", 2); // 버전이 1인지 확인

    request.onupgradeneeded = (event) => {
      const db = event.target.result;

      // 오브젝트 스토어가 없으면 생성
      if (!db.objectStoreNames.contains("conversations")) {
        db.createObjectStore("conversations", {
          keyPath: "id",
          autoIncrement: false,
        });
      }
    };

    request.onsuccess = (event) => {
      resolve(event.target.result);
    };

    request.onerror = (event) => {
      reject("IndexedDB Error: " + event.target.errorCode);
    };
  });
};

// termkey를 이용한 결과 조회 API
export const getTermkeyResult = async (termkey, abortSignal) => {
  try {
    const token = sessionStorage.getItem("accessToken");
    if (!token) {
      throw new Error("인증 토큰이 없습니다. 로그인이 필요합니다.");
    }

    let attempts = 0;

    while (true) {
      if (attempts > 30) throw new Error("시도 횟수가 초과되었습니다.");
      try {
        const response = await axios.get(
          `${serverUrl}/project/temporary/findTemp/${termkey}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            withCredentials: true,
            signal: abortSignal,
          }
        );

        if (!response?.data) {
          throw new Error("응답 데이터가 없습니다.");
        }

        // state가 0이 아닐 때 (처리가 완료되었을 때) 즉시 결과 반환
        if (response.data.state == 1) {
          // console.log("처리 완료, 결과 반환");
          return response.data;
        }

        const randomDelay = Math.floor(
          Math.random() * (5000 - 3000 + 1) + 3000
        ); // 3000ms(3초)에서 5000ms(5초) 사이의 랜덤한 시간
        await new Promise((resolve) => setTimeout(resolve, randomDelay));
      } catch (error) {
        console.error("결과 조회 중 오류 발생:", error);
        throw error;
      }

      attempts++;
    }
  } catch (error) {
    console.error("termkey를 이용한 결과 조회 중 오류:", error);
    throw error;
  }
};

/////////////////////////////////
// App.js
/////////////////////////////////
// 토큰 유효성 검사 API
export const checkTokenValidity = async () => {
  try {
    const token = sessionStorage.getItem("accessToken");
    if (!token) {
      throw new Error("액세스 토큰이 존재하지 않습니다.");
    }

    const response = await axios.get(`${serverUrl}/api/db/token_check`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return response.data;
  } catch (error) {
    console.error("토큰 유효성 검사 오류:", error);
    throw error;
  }
};

// 백엔드 서버 상태 체크 API
export const checkBackendServerStatus = async () => {
  try {
    const response = await axios.get(`${serverUrl}/api/db/back_server`, {
      timeout: 3000, // 3초 타임아웃 설정
    });

    return response.data;
  } catch (error) {
    console.error("백엔드 서버 상태 체크 오류:", error);
    throw error;
  }
};

//! /////////////////////////////
//! AUTODISPLAY API
//! /////////////////////////////
//! Login API functions
//! /////////////////////////////

export const loginNormal = async (email, password) => {
  try {
    const response = await axios.post(
      `${serverUrl}/api/user_autodisplay/login/normal/`,
      {
        email,
        password,
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error during normal login:", error);
    throw error;
  }
};

export const loginMarketing = async (email, password, chatGetId) => {
  try {
    const response = await axios.post(
      `${serverUrl}/api/user/defaultLogin_marketing/`,
      {
        email,
        password,
        chatGetId,
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error during marketing login:", error);
    throw error;
  }
};

export const getUserInfo = async (accessToken) => {
  try {
    const response = await axios.get(
      `${serverUrl}/api/user_autodisplay/userInfo/`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching user info:", error);
    throw error;
  }
};

export const getUserAdminInfo = async (accessToken) => {
  try {
    const response = await axios.get(
      `${serverUrl}/api/user_autodisplay/userAdminInfo/`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching user info:", error);
    throw error;
  }
};

export const getUserMemberType = async (accessToken) => {
  try {
    const response = await axios.get(
      `${serverUrl}/api/user_autodisplay/userMemberType/`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching user info:", error);
    throw error;
  }
};

//! /////////////////////////////
//! Signup API functions
//! /////////////////////////////

export const signupAutodisplay = async (signupData) => {
  try {
    const response = await axios.post(
      `${serverUrl}/api/user_autodisplay/autodisplay_signup/`,
      signupData,
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error during autodisplay signup:", error);
    throw error;
  }
};

export const checkEmailExists = async (email) => {
  try {
    const response = await axios.post(
      `${serverUrl}/api/user_autodisplay/checkEmail/`,
      {
        email,
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error checking email:", error);
    throw error;
  }
};

//! /////////////////////////////
//! Password Change API functions
//! /////////////////////////////

export const changePassword = async (password, current_password) => {
  try {
    const accessToken = sessionStorage.getItem("accessToken");
    if (!accessToken) {
      throw new Error("액세스 토큰이 존재하지 않습니다.");
    }

    const response = await axios.put(
      `${serverUrl}/api/user_autodisplay/passUpdate/`,
      {
        password,
        current_password,
      },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`, // 토큰을 Authorization 헤더에 추가
          "Content-Type": "application/json",
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error changing password:", error);
    throw error;
  }
};

//! /////////////////////////////
//! View API functions
//! ////////////////////////////

//View 생성 api
export const createViewOnServer = async (isLoggedIn) => {
  if (isLoggedIn) {
    try {
      const token = sessionStorage.getItem("accessToken"); // 세션에서 액세스 토큰 가져오기

      if (!token) {
        throw new Error("액세스 토큰이 존재하지 않습니다.");
      }

      const PUT_DATA = {
        createDate: new Date().toLocaleString("ko-KR", {
          timeZone: "Asia/Seoul",
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        }),
      };
      const response = await axios.post(
        `${serverUrl}/autodisplay_tools/view/create_view`,
        PUT_DATA, // POST 요청에 보낼 데이터가 없는 경우 빈 객체 전달
        {
          headers: {
            Authorization: `Bearer ${token}`, // Bearer 토큰을 헤더에 추가
            "Content-Type": "application/json",
          },
          withCredentials: true, // 쿠키와 자격 증명 포함 (필요 시)
        }
      );

      // console.log(response.data.inserted_id);
      return response.data.inserted_id; // 서버로부터 가져온 conversationId 반환
    } catch (error) {
      console.error("Error creating view on server:", error);
      throw error;
    }
  }
};

//View 업데이트 api
export const updateViewOnServer = async (viewId, updateData) => {
  try {
    const token = sessionStorage.getItem("accessToken"); // 액세스 토큰을 세션에서 가져오기
    // console.log("token", token);

    if (!token) {
      throw new Error("액세스 토큰이 존재하지 않습니다.");
    }

    if (!viewId) {
      throw new Error("VIEW ID가 필요합니다.");
    }
    const PUT_DATA = {
      id: viewId,
      ...updateData,
      updateDate: new Date().toLocaleString("ko-KR", {
        timeZone: "Asia/Seoul",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      }),
      timestamp: Date.now(),
    };
    await axios.put(
      `${serverUrl}/autodisplay_tools/view/update_view`,
      PUT_DATA,
      {
        headers: {
          Authorization: `Bearer ${token}`, // Bearer 토큰을 헤더에 추가
          "Content-Type": "application/json",
        },
        withCredentials: true, // 쿠키와 함께 자격 증명을 전달 (optional)
      }
    );
  } catch (error) {
    console.error("Error updating view on server:", error);
  }
};

//View 업데이트 게시관리 api
export const updateViewOnServerPostManagement = async (viewId, updateData) => {
  try {
    const token = sessionStorage.getItem("accessToken"); // 액세스 토큰을 세션에서 가져오기
    // console.log("token", token);

    if (!token) {
      throw new Error("액세스 토큰이 존재하지 않습니다.");
    }

    if (!viewId) {
      throw new Error("VIEW ID가 필요합니다.");
    }
    const PUT_DATA = {
      id: viewId,
      ...updateData,
      // timestamp: Date.now(),
    };
    await axios.put(
      `${serverUrl}/autodisplay_tools/view/update_view`,
      PUT_DATA,
      {
        headers: {
          Authorization: `Bearer ${token}`, // Bearer 토큰을 헤더에 추가
          "Content-Type": "application/json",
        },
        withCredentials: true, // 쿠키와 함께 자격 증명을 전달 (optional)
      }
    );
  } catch (error) {
    console.error("Error updating view on server:", error);
  }
};

// View 리스트 단건 조회
export const getViewDetailOnServer = async (viewId, isLoggedIn) => {
  if (isLoggedIn) {
    try {
      const accessToken = sessionStorage.getItem("accessToken");

      const response = await axios.get(
        `${serverUrl}/autodisplay_tools/view/view_detail/${viewId}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching view detail from server:", error);
      throw error;
    }
  }
};

// View 리스트 조회
export const getViewListOnServer = async (size, page, isLoggedIn) => {
  if (isLoggedIn) {
    try {
      const accessToken = sessionStorage.getItem("accessToken");

      const response = await axios.get(
        `${serverUrl}/autodisplay_tools/view/view_list?size=${size}&page=${page}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching view list from server:", error);
      throw error;
    }
  }
};

// View 리스트 조회
export const getViewListCustomerOnServer = async (
  id,
  size,
  page,
  isLoggedIn
) => {
  if (isLoggedIn) {
    try {
      const accessToken = sessionStorage.getItem("accessToken");

      const response = await axios.get(
        `${serverUrl}/autodisplay_tools/view/view_list_customer?id=${id}&size=${size}&page=${page}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching view list from server:", error);
      throw error;
    }
  }
};

// View 휴지통
export const getViewDeleteListOnServer = async (size, page, isLoggedIn) => {
  if (isLoggedIn) {
    try {
      const accessToken = sessionStorage.getItem("accessToken");

      const response = await axios.get(
        `${serverUrl}/autodisplay_tools/view/delete_view_list?size=${size}&page=${page}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching project delete list from server:", error);
      throw error;
    }
  }
};

// Display 생성 api
export const createDisplayOnServer = async (isLoggedIn) => {
  if (isLoggedIn) {
    try {
      const token = sessionStorage.getItem("accessToken"); // 세션에서 액세스 토큰 가져오기

      if (!token) {
        throw new Error("액세스 토큰이 존재하지 않습니다.");
      }

      const PUT_DATA = {
        createDate: new Date().toLocaleString("ko-KR", {
          timeZone: "Asia/Seoul",
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        }),
      };
      const response = await axios.post(
        `${serverUrl}/autodisplay_tools/device/create_device`,
        PUT_DATA, // POST 요청에 보낼 데이터가 없는 경우 빈 객체 전달
        {
          headers: {
            Authorization: `Bearer ${token}`, // Bearer 토큰을 헤더에 추가
            "Content-Type": "application/json",
          },
          withCredentials: true, // 쿠키와 자격 증명 포함 (필요 시)
        }
      );

      return response.data.inserted_id; // 서버로부터 가져온 deviceId 반환
    } catch (error) {
      console.error("Error creating device on server:", error);
      throw error;
    }
  }
};

//Display 업데이트 api
export const updateDisplayOnServer = async (deviceId, updateData) => {
  try {
    const token = sessionStorage.getItem("accessToken"); // 액세스 토큰을 세션에서 가져오기

    if (!token) {
      throw new Error("액세스 토큰이 존재하지 않습니다.");
    }

    if (!deviceId) {
      throw new Error("DEVICE ID가 필요합니다.");
    }
    const PUT_DATA = {
      id: deviceId,
      ...updateData,
      updateDate: new Date().toLocaleString("ko-KR", {
        timeZone: "Asia/Seoul",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      }),
      timestamp: Date.now(),
    };
    await axios.put(
      `${serverUrl}/autodisplay_tools/device/update_device`,
      PUT_DATA,
      {
        headers: {
          Authorization: `Bearer ${token}`, // Bearer 토큰을 헤더에 추가
          "Content-Type": "application/json",
        },
        withCredentials: true, // 쿠키와 함께 자격 증명을 전달 (optional)
      }
    );
  } catch (error) {
    console.error("Error updating device on server:", error);
  }
};

// display 단건 조회
export const getDisplayDetailOnServer = async (deviceId, isLoggedIn) => {
  if (isLoggedIn) {
    try {
      const accessToken = sessionStorage.getItem("accessToken");

      const response = await axios.get(
        `${serverUrl}/autodisplay_tools/device/device_detail/${deviceId}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching device detail from server:", error);
      throw error;
    }
  }
};

// display 리스트 조회
export const getDisplayListOnServer = async (size, page, isLoggedIn) => {
  if (isLoggedIn) {
    try {
      const accessToken = sessionStorage.getItem("accessToken");

      const response = await axios.get(
        `${serverUrl}/autodisplay_tools/device/device_list?size=${size}&page=${page}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching device list from server:", error);
      throw error;
    }
  }
};

// display 리스트 조회
export const getDisplayListCustomerOnServer = async (
  id,
  size,
  page,
  isLoggedIn
) => {
  if (isLoggedIn) {
    try {
      const accessToken = sessionStorage.getItem("accessToken");

      const response = await axios.get(
        `${serverUrl}/autodisplay_tools/device/device_list_customer?id=${id}&size=${size}&page=${page}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching device list from server:", error);
      throw error;
    }
  }
};

// Display 휴지통
export const getDisplayDeleteListOnServer = async (size, page, isLoggedIn) => {
  if (isLoggedIn) {
    try {
      const accessToken = sessionStorage.getItem("accessToken");

      const response = await axios.get(
        `${serverUrl}/autodisplay_tools/device/delete_device_list?size=${size}&page=${page}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching device delete list from server:", error);
      throw error;
    }
  }
};

//! /////////////////////////////
//! Create Content API functions
//! /////////////////////////////

//정보 업로드 멀티모달(파일 등록)
export const AutoDisplayFileAnalysisMultimodalRequest = async (
  data,
  isLoggedIn,
  abortSignal
) => {
  if (!isLoggedIn) {
    console.error("로그인이 필요합니다.");
    return null;
  }

  try {
    const formData = new FormData();
    data.files.forEach((file) => {
      formData.append("files", file);
    });
    formData.append("time_stamp", data.time_stamp); // 다른 데이터 추가
    formData.append("type", "auto_display_file_analysis_multimodal"); // 다른 데이터 추가

    const token = sessionStorage.getItem("accessToken");
    if (!token) {
      throw new Error("액세스 토큰이 존재하지 않습니다.");
    }

    const response = await axios.post(
      `${serverUrl}/autodisplay_tools/view/create_view_temp_file`,
      formData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
        withCredentials: true,
        signal: abortSignal,
      }
    );

    if (!response.data?.time || !response.data?.objectId) {
      return response.data;
    }

    await new Promise((resolve) => setTimeout(resolve, response.data.time));

    const result = await getTermkeyResult(response.data.objectId, abortSignal);

    return result;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

//정보 업로드 (텍스트 입력)
export const AutoDisplayFileAnalysisRequest = async (data, isLoggedIn) => {
  if (!isLoggedIn) {
    console.error("로그인이 필요합니다.");
    return null;
  }

  try {
    const token = sessionStorage.getItem("accessToken");
    if (!token) {
      throw new Error("액세스 토큰이 존재하지 않습니다.");
    }

    const response = await axios.post(
      `${serverUrl}/autodisplay_tools/view/create_view_temp`,
      data,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        withCredentials: true,
      }
    );

    if (!response.data?.time || !response.data?.objectId) {
      return response.data;
    }

    await new Promise((resolve) => setTimeout(resolve, response.data.time));

    const result = await getTermkeyResult(response.data.objectId);

    return result;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

//콘텐츠 내용 생성
export const AutoDisplayContentGeneratorRequest = async (data, isLoggedIn) => {
  if (!isLoggedIn) {
    console.error("로그인이 필요합니다.");
    return null;
  }

  try {
    const token = sessionStorage.getItem("accessToken");
    if (!token) {
      throw new Error("액세스 토큰이 존재하지 않습니다.");
    }

    const response = await axios.post(
      `${serverUrl}/autodisplay_tools/view/create_view_temp`,
      data,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        withCredentials: true,
      }
    );

    if (!response.data?.time || !response.data?.objectId) {
      return response.data;
    }

    await new Promise((resolve) => setTimeout(resolve, response.data.time));

    const result = await getTermkeyResult(response.data.objectId);

    return result;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

//디자인 생성
export const AutoDisplayDesignGeneratorRequest = async (data, isLoggedIn) => {
  if (!isLoggedIn) {
    console.error("로그인이 필요합니다.");
    return null;
  }

  try {
    const token = sessionStorage.getItem("accessToken");
    if (!token) {
      throw new Error("액세스 토큰이 존재하지 않습니다.");
    }

    const response = await axios.post(
      `${serverUrl}/autodisplay_tools/view/create_view_temp`,
      data,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        withCredentials: true,
      }
    );

    if (!response.data?.time || !response.data?.objectId) {
      return response.data;
    }

    await new Promise((resolve) => setTimeout(resolve, response.data.time));

    const result = await getTermkeyResultDesign(response.data.objectId);

    return result;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

//view 활성화 요청
export const viewDetailActivateRequest = async (data) => {
  try {
    const response = await axios.post(
      `${serverUrl}/autodisplay_tools/view/view_detail_activate`,
      data,
      {
        headers: {
          "Content-Type": "application/json",
        },
        withCredentials: true,
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error activating view detail:", error);
    throw error;
  }
};

// termkey를 이용한 결과 조회 API
export const getTermkeyResultDesign = async (termkey, abortSignal) => {
  try {
    const token = sessionStorage.getItem("accessToken");
    if (!token) {
      throw new Error("인증 토큰이 없습니다. 로그인이 필요합니다.");
    }

    let attempts = 0;

    while (true) {
      if (attempts > 50) throw new Error("시도 횟수가 초과되었습니다.");
      try {
        const response = await axios.get(
          `${serverUrl}/project/temporary/findTemp/${termkey}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            withCredentials: true,
            signal: abortSignal,
          }
        );

        if (!response?.data) {
          throw new Error("응답 데이터가 없습니다.");
        }

        // state가 0이 아닐 때 (처리가 완료되었을 때) 즉시 결과 반환
        if (response.data.state == 1) {
          // console.log("처리 완료, 결과 반환");
          return response.data;
        }

        const randomDelay = Math.floor(
          Math.random() * (5000 - 3000 + 1) + 3000
        ); // 3000ms(3초)에서 5000ms(5초) 사이의 랜덤한 시간
        await new Promise((resolve) => setTimeout(resolve, randomDelay));
      } catch (error) {
        console.error("결과 조회 중 오류 발생:", error);
        throw error;
      }

      attempts++;
    }
  } catch (error) {
    console.error("termkey를 이용한 결과 조회 중 오류:", error);
    throw error;
  }
};

//! /////////////////////////////
//! ViewApi  APi functions
//! /////////////////////////////

//ViewApi 생성 api
export const createViewApiOnServer = async (isLoggedIn) => {
  if (isLoggedIn) {
    try {
      const token = sessionStorage.getItem("accessToken"); // 세션에서 액세스 토큰 가져오기

      if (!token) {
        throw new Error("액세스 토큰이 존재하지 않습니다.");
      }

      const PUT_DATA = {
        createDate: new Date().toLocaleString("ko-KR", {
          timeZone: "Asia/Seoul",
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        }),
      };
      const response = await axios.post(
        `${serverUrl}/autodisplay_tools/view_api/create_view_api`,
        PUT_DATA, // POST 요청에 보낼 데이터가 없는 경우 빈 객체 전달
        {
          headers: {
            Authorization: `Bearer ${token}`, // Bearer 토큰을 헤더에 추가
            "Content-Type": "application/json",
          },
          withCredentials: true, // 쿠키와 자격 증명 포함 (필요 시)
        }
      );

      // console.log(response.data.inserted_id);
      return response.data.inserted_id; // 서버로부터 가져온 conversationId 반환
    } catch (error) {
      console.error("Error creating view on server:", error);
      throw error;
    }
  }
};

//ViewApi 업데이트 api
export const updateViewApiOnServer = async (viewId, updateData) => {
  try {
    const token = sessionStorage.getItem("accessToken"); // 액세스 토큰을 세션에서 가져오기
    // console.log("token", token);

    if (!token) {
      throw new Error("액세스 토큰이 존재하지 않습니다.");
    }

    if (!viewId) {
      throw new Error("VIEW ID가 필요합니다.");
    }
    const PUT_DATA = {
      id: viewId,
      ...updateData,
      updateDate: new Date().toLocaleString("ko-KR", {
        timeZone: "Asia/Seoul",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      }),
      timestamp: Date.now(),
    };
    await axios.put(
      `${serverUrl}/autodisplay_tools/view_api/update_view_api`,
      PUT_DATA,
      {
        headers: {
          Authorization: `Bearer ${token}`, // Bearer 토큰을 헤더에 추가
          "Content-Type": "application/json",
        },
        withCredentials: true, // 쿠키와 함께 자격 증명을 전달 (optional)
      }
    );
  } catch (error) {
    console.error("Error updating viewapi on server:", error);
  }
};

//ViewApi 업데이트 게시관리 api
export const updateViewApiOnServerPostManagement = async (
  viewId,
  updateData
) => {
  try {
    const token = sessionStorage.getItem("accessToken"); // 액세스 토큰을 세션에서 가져오기
    // console.log("token", token);

    if (!token) {
      throw new Error("액세스 토큰이 존재하지 않습니다.");
    }

    if (!viewId) {
      throw new Error("VIEW ID가 필요합니다.");
    }
    const PUT_DATA = {
      id: viewId,
      ...updateData,
      // timestamp: Date.now(),
    };
    await axios.put(
      `${serverUrl}/autodisplay_tools/view_api/update_view_api`,
      PUT_DATA,
      {
        headers: {
          Authorization: `Bearer ${token}`, // Bearer 토큰을 헤더에 추가
          "Content-Type": "application/json",
        },
        withCredentials: true, // 쿠키와 함께 자격 증명을 전달 (optional)
      }
    );
  } catch (error) {
    console.error("Error updating view on server:", error);
  }
};

// ViewApi 리스트 단건 조회
export const getViewApiDetailOnServer = async (viewId, isLoggedIn) => {
  try {
    const accessToken = sessionStorage.getItem("accessToken");

    const response = await axios.get(
      `${serverUrl}/autodisplay_tools/view_api/view_detail_api/${viewId}`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching view detail from server:", error);
    throw error;
  }
};

//view api 활성화 요청
export const viewApiDetailActivateRequest = async (data) => {
  try {
    const response = await axios.post(
      `${serverUrl}/autodisplay_tools/view_api/view_detail_activate_api`,
      data,
      {
        headers: {
          "Content-Type": "application/json",
        },
        withCredentials: true,
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error activating view detail:", error);
    throw error;
  }
};

// ViewApi 리스트 조회
export const getViewApiListOnServer = async (size, page, isLoggedIn) => {
  if (isLoggedIn) {
    try {
      const accessToken = sessionStorage.getItem("accessToken");

      const response = await axios.get(
        `${serverUrl}/autodisplay_tools/view_api/view_list_api?size=${size}&page=${page}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching view list from server:", error);
      throw error;
    }
  }
};

// ViewApi 리스트 조회
export const getViewApiListCustomerOnServer = async (
  id,
  size,
  page,
  isLoggedIn
) => {
  if (isLoggedIn) {
    try {
      const accessToken = sessionStorage.getItem("accessToken");

      const response = await axios.get(
        `${serverUrl}/autodisplay_tools/view_api/view_list_api_customer?id=${id}&size=${size}&page=${page}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching view list from server:", error);
      throw error;
    }
  }
};

// ViewApi 휴지통
export const getViewApiDeleteListOnServer = async (size, page, isLoggedIn) => {
  if (isLoggedIn) {
    try {
      const accessToken = sessionStorage.getItem("accessToken");

      const response = await axios.get(
        `${serverUrl}/autodisplay_tools/view_api/delete_view_list_api?size=${size}&page=${page}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching project delete list from server:", error);
      throw error;
    }
  }
};

//! /////////////////////////////
//! View Custom functions
//! /////////////////////////////

//ViewCustom 생성 api
export const createViewCustomOnServer = async (isLoggedIn) => {
  if (isLoggedIn) {
    try {
      const token = sessionStorage.getItem("accessToken"); // 세션에서 액세스 토큰 가져오기

      if (!token) {
        throw new Error("액세스 토큰이 존재하지 않습니다.");
      }

      const PUT_DATA = {
        createDate: new Date().toLocaleString("ko-KR", {
          timeZone: "Asia/Seoul",
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        }),
      };
      const response = await axios.post(
        `${serverUrl}/autodisplay_tools/view_custom/create_view_custom`,
        PUT_DATA, // POST 요청에 보낼 데이터가 없는 경우 빈 객체 전달
        {
          headers: {
            Authorization: `Bearer ${token}`, // Bearer 토큰을 헤더에 추가
            "Content-Type": "application/json",
          },
          withCredentials: true, // 쿠키와 자격 증명 포함 (필요 시)
        }
      );

      // console.log(response.data.inserted_id);
      return response.data.inserted_id; // 서버로부터 가져온 conversationId 반환
    } catch (error) {
      console.error("Error creating view on server:", error);
      throw error;
    }
  }
};

//ViewCustom 업데이트 api
export const updateViewCustomOnServer = async (viewId, updateData) => {
  try {
    const token = sessionStorage.getItem("accessToken"); // 액세스 토큰을 세션에서 가져오기
    // console.log("token", token);

    if (!token) {
      throw new Error("액세스 토큰이 존재하지 않습니다.");
    }

    if (!viewId) {
      throw new Error("VIEW ID가 필요합니다.");
    }
    const PUT_DATA = {
      id: viewId,
      ...updateData,
      updateDate: new Date().toLocaleString("ko-KR", {
        timeZone: "Asia/Seoul",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      }),
      timestamp: Date.now(),
    };
    await axios.put(
      `${serverUrl}/autodisplay_tools/view_custom/update_view_custom`,
      PUT_DATA,
      {
        headers: {
          Authorization: `Bearer ${token}`, // Bearer 토큰을 헤더에 추가
          "Content-Type": "application/json",
        },
        withCredentials: true, // 쿠키와 함께 자격 증명을 전달 (optional)
      }
    );
  } catch (error) {
    console.error("Error updating view on server:", error);
  }
};

//ViewCustom 업데이트 게시관리 api
export const updateViewCustomOnServerPostManagement = async (
  viewId,
  updateData
) => {
  try {
    const token = sessionStorage.getItem("accessToken"); // 액세스 토큰을 세션에서 가져오기
    // console.log("token", token);

    if (!token) {
      throw new Error("액세스 토큰이 존재하지 않습니다.");
    }

    if (!viewId) {
      throw new Error("VIEW ID가 필요합니다.");
    }
    const PUT_DATA = {
      id: viewId,
      ...updateData,
      // timestamp: Date.now(),
    };
    await axios.put(
      `${serverUrl}/autodisplay_tools/view_custom/update_view_custom`,
      PUT_DATA,
      {
        headers: {
          Authorization: `Bearer ${token}`, // Bearer 토큰을 헤더에 추가
          "Content-Type": "application/json",
        },
        withCredentials: true, // 쿠키와 함께 자격 증명을 전달 (optional)
      }
    );
  } catch (error) {
    console.error("Error updating view on server:", error);
  }
};

// ViewCustom 리스트 단건 조회
export const getViewCustomDetailOnServer = async (viewId, isLoggedIn) => {
  if (isLoggedIn) {
    try {
      const accessToken = sessionStorage.getItem("accessToken");

      const response = await axios.get(
        `${serverUrl}/autodisplay_tools/view_custom/view_detail_custom/${viewId}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching view detail from server:", error);
      throw error;
    }
  }
};

// ViewCustom 리스트 조회
export const getViewCustomListOnServer = async (size, page, isLoggedIn) => {
  if (isLoggedIn) {
    try {
      const accessToken = sessionStorage.getItem("accessToken");

      const response = await axios.get(
        `${serverUrl}/autodisplay_tools/view_custom/view_list_custom?size=${size}&page=${page}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching view list from server:", error);
      throw error;
    }
  }
};

// ViewCustom 리스트 조회
export const getViewCustomListCustomerOnServer = async (
  id,
  size,
  page,
  isLoggedIn
) => {
  if (isLoggedIn) {
    try {
      const accessToken = sessionStorage.getItem("accessToken");

      const response = await axios.get(
        `${serverUrl}/autodisplay_tools/view_custom/view_list_custom_customer?id=${id}&size=${size}&page=${page}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching view list from server:", error);
      throw error;
    }
  }
};

// ViewCustom 휴지통
export const getViewCustomDeleteListOnServer = async (
  size,
  page,
  isLoggedIn
) => {
  if (isLoggedIn) {
    try {
      const accessToken = sessionStorage.getItem("accessToken");

      const response = await axios.get(
        `${serverUrl}/autodisplay_tools/view_custom/delete_view_list_custom?size=${size}&page=${page}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching project delete list from server:", error);
      throw error;
    }
  }
};

//! /////////////////////////////
//! View Custom jungrok functions
//! /////////////////////////////

export const getPlalionSensorData = async (data) => {
  try {
    const response = await axios.post(
      `${serverUrl}/autodisplay_tools/proxy/plalion_sensor_data`,
      data,
      {
        headers: {
          "Content-Type": "application/json",
        },
        withCredentials: true,
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error activating view detail:", error);
    throw error;
  }
};

//! /////////////////////////////
//! View API Weather functions
//! /////////////////////////////

export const getWeatherData = async (params) => {
  const serviceKey = process.env.REACT_APP_WEATHER_API_KEY;

  params.serviceKey = serviceKey;

  try {
    const response = await axios.get(
      "https://apis.data.go.kr/1360000/VilageFcstInfoService_2.0/getUltraSrtNcst",
      { params }
    );

    return response;
  } catch (error) {
    console.error("Error fetching weather data:", error);
    throw error;
  }
};

export const getWeatherForecastData = async (params) => {
  const serviceKey = process.env.REACT_APP_WEATHER_API_KEY;

  params.serviceKey = serviceKey;

  try {
    const response = await axios.get(
      "https://apis.data.go.kr/1360000/VilageFcstInfoService_2.0/getVilageFcst",
      { params }
    );

    return response;
  } catch (error) {
    console.error("Error fetching weather data:", error);
    throw error;
  }
};

export const getOpenWeatherData = async (params) => {
  const serviceKey = process.env.REACT_APP_OPEN_WEATHER_API_KEY

  try {
    const response = await axios.get(
      `https://api.openweathermap.org/data/3.0/onecall?lat=${params.lat}&lon=${params.lon}&appid=${serviceKey}&units=metric`,
    );

    return response;
  } catch (error) {
    console.error("Error fetching weather data:", error);
    throw error;
  }
};

//! /////////////////////////////
//! 고객사 등록
//! /////////////////////////////

//고객사 등록 api
export const createCustomerOnServer = async (isLoggedIn) => {
  if (isLoggedIn) {
    try {
      const token = sessionStorage.getItem("accessToken"); // 세션에서 액세스 토큰 가져오기

      if (!token) {
        throw new Error("액세스 토큰이 존재하지 않습니다.");
      }

      const PUT_DATA = {
        createDate: new Date().toLocaleString("ko-KR", {
          timeZone: "Asia/Seoul",
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        }),
      };
      const response = await axios.post(
        `${serverUrl}/autodisplay_tools/customer/create_customer`,
        PUT_DATA, // POST 요청에 보낼 데이터가 없는 경우 빈 객체 전달
        {
          headers: {
            Authorization: `Bearer ${token}`, // Bearer 토큰을 헤더에 추가
            "Content-Type": "application/json",
          },
          withCredentials: true, // 쿠키와 자격 증명 포함 (필요 시)
        }
      );

      // console.log(response.data.inserted_id);
      return response.data.inserted_id; // 서버로부터 가져온 conversationId 반환
    } catch (error) {
      console.error("Error creating view on server:", error);
      throw error;
    }
  }
};

//고객사 업데이트 api
export const updateCustomerOnServer = async (viewId, updateData) => {
  try {
    const token = sessionStorage.getItem("accessToken"); // 액세스 토큰을 세션에서 가져오기
    // console.log("token", token);

    if (!token) {
      throw new Error("액세스 토큰이 존재하지 않습니다.");
    }

    if (!viewId) {
      throw new Error("VIEW ID가 필요합니다.");
    }
    const PUT_DATA = {
      id: viewId,
      ...updateData,
      updateDate: new Date().toLocaleString("ko-KR", {
        timeZone: "Asia/Seoul",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      }),
      timestamp: Date.now(),
    };
    await axios.put(
      `${serverUrl}/autodisplay_tools/customer/update_customer`,
      PUT_DATA,
      {
        headers: {
          Authorization: `Bearer ${token}`, // Bearer 토큰을 헤더에 추가
          "Content-Type": "application/json",
        },
        withCredentials: true, // 쿠키와 함께 자격 증명을 전달 (optional)
      }
    );
  } catch (error) {
    console.error("Error updating view on server:", error);
  }
};

// 고객사 리스트 단건 조회
export const getCustomerDetailOnServer = async (viewId, isLoggedIn) => {
  if (isLoggedIn) {
    try {
      const accessToken = sessionStorage.getItem("accessToken");

      const response = await axios.get(
        `${serverUrl}/autodisplay_tools/customer/customer_detail/${viewId}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching view detail from server:", error);
      throw error;
    }
  }
};

// 고객사 리스트 조회
export const getCustomerListOnServer = async (
  customerType,
  size,
  page,
  isLoggedIn
) => {
  if (isLoggedIn) {
    try {
      const accessToken = sessionStorage.getItem("accessToken");

      const response = await axios.get(
        `${serverUrl}/autodisplay_tools/customer/customer_list?customer_type=${customerType}&size=${size}&page=${page}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching view list from server:", error);
      throw error;
    }
  }
};

// 고객사 휴지통
export const getCustomerDeleteListOnServer = async (size, page, isLoggedIn) => {
  if (isLoggedIn) {
    try {
      const accessToken = sessionStorage.getItem("accessToken");

      const response = await axios.get(
        `${serverUrl}/autodisplay_tools/customer/delete_customer_list?size=${size}&page=${page}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching project delete list from server:", error);
      throw error;
    }
  }
};

//정보 업로드 멀티모달(파일 등록)
export const AutoDisplaySummaryContentGeneratorMultimodalRequest = async (
  data,
  isLoggedIn,
  abortSignal
) => {
  if (!isLoggedIn) {
    console.error("로그인이 필요합니다.");
    return null;
  }

  try {
    const formData = new FormData();
    data.files.forEach((file) => {
      formData.append("files", file);
    });
    formData.append("time_stamp", data.time_stamp); // 다른 데이터 추가
    formData.append(
      "type",
      "auto_display_summary_content_generator_multimodal"
    ); // 다른 데이터 추가
    formData.append("message_target", data.message_target);
    formData.append("target_language", data.target_language);

    const token = sessionStorage.getItem("accessToken");
    if (!token) {
      throw new Error("액세스 토큰이 존재하지 않습니다.");
    }

    const response = await axios.post(
      `${serverUrl}/autodisplay_tools/view/create_view_temp_file`,
      formData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
        withCredentials: true,
        signal: abortSignal,
      }
    );

    if (!response.data?.time || !response.data?.objectId) {
      return response.data;
    }

    await new Promise((resolve) => setTimeout(resolve, response.data.time));

    const result = await getTermkeyResult(response.data.objectId, abortSignal);

    return result;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

//정보 업로드 (텍스트 입력)
export const AutoDisplaySummaryContentGeneratorRequest = async (
  data,
  isLoggedIn
) => {
  if (!isLoggedIn) {
    console.error("로그인이 필요합니다.");
    return null;
  }

  try {
    const token = sessionStorage.getItem("accessToken");
    if (!token) {
      throw new Error("액세스 토큰이 존재하지 않습니다.");
    }

    const response = await axios.post(
      `${serverUrl}/autodisplay_tools/view/create_view_temp`,
      data,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        withCredentials: true,
      }
    );

    if (!response.data?.time || !response.data?.objectId) {
      return response.data;
    }

    await new Promise((resolve) => setTimeout(resolve, response.data.time));

    const result = await getTermkeyResult(response.data.objectId);

    return result;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

//view 활성화 체크
export const viewCheckActivateStateRequest = async (data) => {
  try {
    const response = await axios.post(
      `${serverUrl}/autodisplay_tools/view/view_check_activate_state`,
      data,
      {
        headers: {
          "Content-Type": "application/json",
        },
        withCredentials: true,
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error activating view detail:", error);
    throw error;
  }
};

//view 업데이트 체크
export const viewCheckUpdateStateRequest = async (data) => {
  try {
    const response = await axios.post(
      `${serverUrl}/autodisplay_tools/view/view_check_update_state`,
      data,
      {
        headers: {
          "Content-Type": "application/json",
        },
        withCredentials: true,
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error activating view detail:", error);
    throw error;
  }
};

//view custom 활성화 체크
export const viewCheckActivateStateApiRequest = async (data) => {
  try {
    const response = await axios.post(
      `${serverUrl}/autodisplay_tools/view_api/view_check_activate_state_api`,
      data,
      {
        headers: {
          "Content-Type": "application/json",
        },
        withCredentials: true,
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error activating view detail:", error);
    throw error;
  }
};

//view custom 업데이트 체크
export const viewCheckUpdateStateApiRequest = async (data) => {
  try {
    const response = await axios.post(
      `${serverUrl}/autodisplay_tools/view_api/view_check_update_state_api`,
      data,
      {
        headers: {
          "Content-Type": "application/json",
        },
        withCredentials: true,
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error activating view detail:", error);
    throw error;
  }
};

//view custom 활성화 체크
export const viewCheckActivateStateCustomRequest = async (data) => {
  try {
    const response = await axios.post(
      `${serverUrl}/autodisplay_tools/view_custom/view_check_activate_state_custom`,
      data,
      {
        headers: {
          "Content-Type": "application/json",
        },
        withCredentials: true,
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error activating view detail:", error);
    throw error;
  }
};

//view custom 업데이트 체크
export const viewCheckUpdateStateCustomRequest = async (data) => {
  try {
    const response = await axios.post(
      `${serverUrl}/autodisplay_tools/view_custom/view_check_update_state_custom`,
      data,
      {
        headers: {
          "Content-Type": "application/json",
        },
        withCredentials: true,
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error activating view detail:", error);
    throw error;
  }
};

//view link 활성화 체크
export const viewDetailActivateLinkRequest = async (data) => {
  try {
    const response = await axios.post(
      `${serverUrl}/autodisplay_tools/view_custom/view_detail_activate_link`,
      data,
      {
        headers: {
          "Content-Type": "application/json",
        },
        withCredentials: true,
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error activating view detail:", error);
    throw error;
  }
};

//view link 업데이트 체크
export const viewCheckUpdateStateLinkRequest = async (data) => {
  try {
    const response = await axios.post(
      `${serverUrl}/autodisplay_tools/view_custom/view_check_update_state_link`,
      data,
      {
        headers: {
          "Content-Type": "application/json",
        },
        withCredentials: true,
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error activating view detail:", error);
    throw error;
  }
};

/* ****************************************************** */
/* Edu View */
/* ****************************************************** */

//View 생성 api
export const createEduViewOnServer = async (isLoggedIn) => {
  if (isLoggedIn) {
    try {
      const token = sessionStorage.getItem("accessToken"); // 세션에서 액세스 토큰 가져오기

      if (!token) {
        throw new Error("액세스 토큰이 존재하지 않습니다.");
      }

      const PUT_DATA = {
        createDate: new Date().toLocaleString("ko-KR", {
          timeZone: "Asia/Seoul",
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        }),
      };
      const response = await axios.post(
        `${serverUrl}/autodisplay_tools/view_edu/create_view_edu`,
        PUT_DATA, // POST 요청에 보낼 데이터가 없는 경우 빈 객체 전달
        {
          headers: {
            Authorization: `Bearer ${token}`, // Bearer 토큰을 헤더에 추가
            "Content-Type": "application/json",
          },
          withCredentials: true, // 쿠키와 자격 증명 포함 (필요 시)
        }
      );

      // console.log(response.data.inserted_id);
      return response.data.inserted_id; // 서버로부터 가져온 conversationId 반환
    } catch (error) {
      console.error("Error creating view on server:", error);
      throw error;
    }
  }
};

//View 업데이트 api
export const updateEduViewOnServer = async (viewId, updateData) => {
  try {
    const token = sessionStorage.getItem("accessToken"); // 액세스 토큰을 세션에서 가져오기
    // console.log("token", token);

    if (!token) {
      throw new Error("액세스 토큰이 존재하지 않습니다.");
    }

    if (!viewId) {
      throw new Error("VIEW ID가 필요합니다.");
    }
    const PUT_DATA = {
      id: viewId,
      ...updateData,
      updateDate: new Date().toLocaleString("ko-KR", {
        timeZone: "Asia/Seoul",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      }),
      timestamp: Date.now(),
    };
    await axios.put(
      `${serverUrl}/autodisplay_tools/view_edu/update_view_edu`,
      PUT_DATA,
      {
        headers: {
          Authorization: `Bearer ${token}`, // Bearer 토큰을 헤더에 추가
          "Content-Type": "application/json",
        },
        withCredentials: true, // 쿠키와 함께 자격 증명을 전달 (optional)
      }
    );
  } catch (error) {
    console.error("Error updating view on server:", error);
  }
};

// ViewApi 리스트 단건 조회
export const getEduViewDetailOnServer = async (viewId, isLoggedIn) => {
  try {
    const accessToken = sessionStorage.getItem("accessToken");

    const response = await axios.get(
      `${serverUrl}/autodisplay_tools/view_edu/view_detail_edu/${viewId}`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching view detail from server:", error);
    throw error;
  }
};

// View 리스트 조회
export const getEduViewListOnServer = async (size, page, isLoggedIn) => {
  if (isLoggedIn) {
    try {
      const accessToken = sessionStorage.getItem("accessToken");

      const response = await axios.get(
        `${serverUrl}/autodisplay_tools/view_edu/view_list_edu?size=${size}&page=${page}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching view list from server:", error);
      throw error;
    }
  }
};

// View 리스트 조회
export const getEduViewListCustomerOnServer = async (
  id,
  size,
  page,
  isLoggedIn
) => {
  if (isLoggedIn) {
    try {
      const accessToken = sessionStorage.getItem("accessToken");

      const response = await axios.get(
        `${serverUrl}/autodisplay_tools/view_edu/view_list_edu_customer?id=${id}&size=${size}&page=${page}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching view list from server:", error);
      throw error;
    }
  }
};

// ViewApi 휴지통
export const getEduViewDeleteListOnServer = async (size, page, isLoggedIn) => {
  if (isLoggedIn) {
    try {
      const accessToken = sessionStorage.getItem("accessToken");

      const response = await axios.get(
        `${serverUrl}/autodisplay_tools/view_edu/view_delete_list_edu?size=${size}&page=${page}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching project delete list from server:", error);
      throw error;
    }
  }
};

//view 활성화 요청
export const viewEduDetailActivateRequest = async (data) => {
  try {
    const response = await axios.post(
      `${serverUrl}/autodisplay_tools/view_edu/view_detail_activate_edu`,
      data,
      {
        headers: {
          "Content-Type": "application/json",
        },
        withCredentials: true,
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error activating view detail:", error);
    throw error;
  }
};

//view custom 활성화 체크
export const viewCheckActivateStateEduRequest = async (data) => {
  try {
    const response = await axios.post(
      `${serverUrl}/autodisplay_tools/view_edu/view_check_activate_state_edu`,
      data,
      {
        headers: {
          "Content-Type": "application/json",
        },
        withCredentials: true,
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error activating view detail:", error);
    throw error;
  }
};

//view custom 업데이트 체크
export const viewCheckUpdateStateEduRequest = async (data) => {
  try {
    const response = await axios.post(
      `${serverUrl}/autodisplay_tools/view_edu/view_check_update_state_edu`,
      data,
      {
        headers: {
          "Content-Type": "application/json",
        },
        withCredentials: true,
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error activating view detail:", error);
    throw error;
  }
};

//view link 활성화 체크
export const viewDetailActivateLinkEduRequest = async (data) => {
  try {
    const response = await axios.post(
      `${serverUrl}/autodisplay_tools/view_edu/view_detail_activate_link_edu`,
      data,
      {
        headers: {
          "Content-Type": "application/json",
        },
        withCredentials: true,
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error activating view detail:", error);
    throw error;
  }
};

//view link 업데이트 체크
export const viewCheckUpdateStateLinkEduRequest = async (data) => {
  try {
    const response = await axios.post(
      `${serverUrl}/autodisplay_tools/view_edu/view_check_update_state_link_edu`,
      data,
      {
        headers: {
          "Content-Type": "application/json",
        },
        withCredentials: true,
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error activating view detail:", error);
    throw error;
  }
};

//급식표 분석
export const AutoDisplayFileAnalysisMealRequest = async (data) => {
  try {
    const token = sessionStorage.getItem("accessToken");
    if (!token) {
      throw new Error("액세스 토큰이 존재하지 않습니다.");
    }

    const response = await axios.post(
      `${serverUrl}/autodisplay_tools/view/create_view_temp`,
      data,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        withCredentials: true,
      }
    );

    if (!response.data?.time || !response.data?.objectId) {
      return response.data;
    }

    await new Promise((resolve) => setTimeout(resolve, response.data.time));

    const result = await getTermkeyResult(response.data.objectId);

    return result;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

/* ****************************************************** */
/* eduBoard */
/* ****************************************************** */

// 고객사 등록 api
export const createEduBoardOnServer = async (isLoggedIn) => {
  if (isLoggedIn) {
    try {
      const token = sessionStorage.getItem("accessToken"); // 세션에서 액세스 토큰 가져오기

      if (!token) {
        throw new Error("액세스 토큰이 존재하지 않습니다.");
      }

      const PUT_DATA = {
        createDate: new Date().toLocaleString("ko-KR", {
          timeZone: "Asia/Seoul",
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        }),
      };
      const response = await axios.post(
        `${serverUrl}/autodisplay_tools/eduboard/create_eduboard`,
        PUT_DATA, // POST 요청에 보낼 데이터가 없는 경우 빈 객체 전달
        {
          headers: {
            Authorization: `Bearer ${token}`, // Bearer 토큰을 헤더에 추가
            "Content-Type": "application/json",
          },
          withCredentials: true, // 쿠키와 자격 증명 포함 (필요 시)
        }
      );

      // console.log(response.data.inserted_id);
      return response.data.inserted_id; // 서버로부터 가져온 conversationId 반환
    } catch (error) {
      console.error("Error creating view on server:", error);
      throw error;
    }
  }
};

//고객사 업데이트 api
export const updateEduBoardOnServer = async (viewId, updateData) => {
  try {
    const token = sessionStorage.getItem("accessToken"); // 액세스 토큰을 세션에서 가져오기
    // console.log("token", token);

    if (!token) {
      throw new Error("액세스 토큰이 존재하지 않습니다.");
    }

    if (!viewId) {
      throw new Error("VIEW ID가 필요합니다.");
    }
    const PUT_DATA = {
      id: viewId,
      ...updateData,
      updateDate: new Date().toLocaleString("ko-KR", {
        timeZone: "Asia/Seoul",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      }),
      timestamp: Date.now(),
    };
    await axios.put(
      `${serverUrl}/autodisplay_tools/eduboard/update_eduboard`,
      PUT_DATA,
      {
        headers: {
          Authorization: `Bearer ${token}`, // Bearer 토큰을 헤더에 추가
          "Content-Type": "application/json",
        },
        withCredentials: true, // 쿠키와 함께 자격 증명을 전달 (optional)
      }
    );
  } catch (error) {
    console.error("Error updating view on server:", error);
  }
};

// 고객사 리스트 단건 조회
export const getEduBoardDetailOnServer = async (viewId, isLoggedIn) => {
  if (isLoggedIn) {
    try {
      const accessToken = sessionStorage.getItem("accessToken");

      const response = await axios.get(
        `${serverUrl}/autodisplay_tools/eduboard/eduboard_detail/${viewId}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching view detail from server:", error);
      throw error;
    }
  }
};

// 고객사 리스트 조회
export const getEduBoardListOnServer = async (size, page, isLoggedIn) => {
  if (isLoggedIn) {
    try {
      const accessToken = sessionStorage.getItem("accessToken");

      const response = await axios.get(
        `${serverUrl}/autodisplay_tools/eduboard/eduboard_list?size=${size}&page=${page}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching view list from server:", error);
      throw error;
    }
  }
};

// 고객사 휴지통
export const getEduBoardDeleteListOnServer = async (size, page, isLoggedIn) => {
  if (isLoggedIn) {
    try {
      const accessToken = sessionStorage.getItem("accessToken");

      const response = await axios.get(
        `${serverUrl}/autodisplay_tools/eduboard/eduboard_delete_list?size=${size}&page=${page}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching project delete list from server:", error);
      throw error;
    }
  }
};

// 같은학교 선생님 리스트
export const getOrganizationUserListOnServer = async () => {
  try {
    const accessToken = sessionStorage.getItem("accessToken");

    const response = await axios.get(
      `${serverUrl}/api/user_autodisplay/get_organization_user_list/`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching organization user list from server:", error);
    throw error;
  }
};

/* ****************************************************** */
/* eduTimetable */
/* ****************************************************** */

// 타임 테이블 생성
export const createEduTimetableOnServer = async () => {
  try {
    const token = sessionStorage.getItem("accessToken");
    if (!token) {
      throw new Error("액세스 토큰이 존재하지 않습니다.");
    }

    const PUT_DATA = {
      createDate: new Date().toLocaleString("ko-KR", {
        timeZone: "Asia/Seoul",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      }),
    };

    const response = await axios.post(
      `${serverUrl}/autodisplay_tools/timetable/create_timetable`,
      PUT_DATA,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        withCredentials: true,
      }
    );
    return response.data.inserted_id;
  } catch (error) {
    console.error("Error creating timetable on server:", error);
    throw error;
  }
};

// 타임 테이블 업데이트
export const updateEduTimetableOnServer = async (id, updateData) => {
  try {
    const token = sessionStorage.getItem("accessToken");
    if (!token) {
      throw new Error("액세스 토큰이 존재하지 않습니다.");
    }

    const PUT_DATA = {
      id: id,
      ...updateData,
      updateDate: new Date().toLocaleString("ko-KR", {
        timeZone: "Asia/Seoul",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      }),
      timestamp: Date.now(),
    };
    await axios.put(
      `${serverUrl}/autodisplay_tools/timetable/update_timetable`,
      PUT_DATA,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        withCredentials: true,
      }
    );
  } catch (error) {
    console.error("Error updating timetable on server:", error);
  }
};

// 타임테이블 리스트 조회
export const getEduTimetableListOnServer = async (size, page) => {
  try {
    const accessToken = sessionStorage.getItem("accessToken");

    const response = await axios.get(
      `${serverUrl}/autodisplay_tools/timetable/timetable_list?size=${size}&page=${page}`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching timetable list from server:", error);
    throw error;
  }
};

// 타임테이블 단건 조회
export const getEduTimetableDetailOnServer = async (tableId) => {
  try {
    const accessToken = sessionStorage.getItem("accessToken");

    const response = await axios.get(
      `${serverUrl}/autodisplay_tools/timetable/timetable_detail/tableId=${tableId}`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching timetable detail from server:", error);
    throw error;
  }
};

// 타임테이블 휴지통
export const getEduTimetableDeleteListOnServer = async (size, page) => {
  try {
    const accessToken = sessionStorage.getItem("accessToken");

    const response = await axios.get(
      `${serverUrl}/autodisplay_tools/timetable/timetable_delete_list?size=${size}&page=${page}`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching timetable delete list from server:", error);
    throw error;
  }
};

// 로테이션 리스트 조회
export const getEduBoardLotationListOnServer = async (size, page) => {
  try {
    const accessToken = sessionStorage.getItem("accessToken");

    const response = await axios.get(
      `${serverUrl}/autodisplay_tools/eduboard/eduboard_lotation_list?size=${size}&page=${page}`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching view list from server:", error);
    throw error;
  }
};

// 로테이션 단건 조회
export const getEduTimetableLotationDetailOnServer = async (boardId) => {
  try {
    const accessToken = sessionStorage.getItem("accessToken");

    const response = await axios.get(
      `${serverUrl}/autodisplay_tools/timetable/timetable_detail_lotation/${boardId}`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching timetable detail from server:", error);
    throw error;
  }
};

//로테이션 link edu 활성화 체크
export const viewDetailActivateLinkEduLotationRequest = async (data) => {
  try {
    const response = await axios.post(
      `${serverUrl}/autodisplay_tools/timetable/view_detail_activate_link_edu_lotation`, 
      data,
      {
        headers: {
          "Content-Type": "application/json",
        },
        withCredentials: true,
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error activating view detail:", error);
    throw error;
  }
};

//로테이션 link 활성화 체크
export const viewDetailActivateLinkLotationRequest = async (data) => {
  try {
    const response = await axios.post(
      `${serverUrl}/autodisplay_tools/timetable/view_detail_activate_link_lotation`, 
      data,
      {
        headers: {
          "Content-Type": "application/json",
        },
        withCredentials: true,
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error activating view detail:", error);
    throw error;
  }
};